import { Stack } from "@volca/ui/components/ui/stack";
import { Text } from "@volca/ui/components/ui/text";
import { cn } from "@volca/ui/lib/utils";
import { useReportCurrency } from "@/profitability/currency";
import { showValue } from "@/profitability/missing-value";
import { ReportMetadata } from "@/profitability/report-presentation";
import type {
  ReportColumn,
  ReportDataset,
  ReportRow,
} from "@/profitability/types";
import { formatFigureValue, formatReportValue, groupParts } from "./utils";

/**
 * A summary block. One figure fills the tile; grouping turns the same block
 * into a short list, so the tile keeps its place in the grid either way.
 */
export const ReportFigures = ({
  rows,
  groups,
  dataset,
  column,
  field,
  compact = false,
}: {
  rows: ReportRow[];
  groups: string[];
  dataset: ReportDataset;
  column: ReportColumn;
  field: string;
  /** A figure in the supporting strip, one step down in size. */
  compact?: boolean;
}) => {
  const currency = useReportCurrency();
  if (!groups.length)
    return (
      <Stack gap={0} align="stretch" className={cn({ "px-4 pb-4": !compact })}>
        <Text
          variant={compact ? "title" : "stat"}
          className="truncate"
          title={formatReportValue(rows[0][field], column, currency)}
        >
          {showValue(formatFigureValue(rows[0][field], column, currency))}
        </Text>
      </Stack>
    );
  return (
    <Stack
      gap={1}
      align="stretch"
      className={cn("w-full", "max-h-56 overflow-auto", {
        "px-4 pb-4": !compact,
      })}
    >
      {rows.map((row, index) => (
        <Stack
          className="w-full"
          key={index}
          direction="row"
          justify="between"
          align="baseline"
          gap={2}
        >
          <Text variant="caption" tone="muted" className="min-w-0">
            <ReportMetadata>
              {groupParts(row, groups, dataset, currency)}
            </ReportMetadata>
          </Text>
          <Text
            variant="title"
            className="flex-none"
            title={formatReportValue(row[field], column, currency)}
          >
            {showValue(formatFigureValue(row[field], column, currency))}
          </Text>
        </Stack>
      ))}
    </Stack>
  );
};
